/**
 * Generates PDF for Claim Form B (Cashless Insurance) by writing directly on PDF template
 * Uses pdf-lib to write data directly onto the PDF form
 */

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// Import the PDF template
import pdfTemplate from './pdf/final-from-b.pdf';

// Helper to format date as DD/MM/YY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    // Handle different date formats
    let date;
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        // Assume DD/MM/YYYY format
        date = new Date(parts[2], parts[1] - 1, parts[0]);
      }
    } else {
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) return dateStr;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

// Helper to get today's date in DD/MM/YYYY format
const formatTodayDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = String(today.getFullYear());
  return `${day}/${month}/${year}`;
};

// Helper to format time as HH:MM
const formatTime = (timeStr) => {
  if (!timeStr) return '';
  try {
    return timeStr.substring(0, 5);
  } catch {
    return timeStr;
  }
};

// Helper function to convert ISO date to DD/MM/YYYY format
const formatISODate = (isoDate) => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${day}/${month}/${year}`;
  } catch {
    return isoDate;
  }
};

// Helper to zero-pad numbers
const zeroPad = (num, length) => {
  const str = String(num || '').replace(/\D/g, '');
  return str.padStart(length, '0');
};

// Helper to format name as "LastName, FirstName MiddleName"
const formatName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[1]}, ${parts[0]}`;
  // For 3+ parts: last part is last name, first is first name, rest are middle names
  const lastName = parts[parts.length - 1];
  const firstName = parts[0];
  const middleNames = parts.slice(1, -1).join(' ');
  return `${lastName}, ${firstName}${middleNames ? ' ' + middleNames : ''}`;
};

// Convert your API response structure to the format expected by the PDF writer
const transformData = (inputData) => {
  // Handle both API response format and direct form data
  let claimFormB;
  
  if (inputData.success && inputData.data && Array.isArray(inputData.data)) {
    // API response format
    claimFormB = inputData.data[0];
  } else if (inputData.claimFormB) {
    // Test data format
    claimFormB = inputData.claimFormB;
  } else {
    // Direct form data
    claimFormB = inputData;
  }

  // Debug: Log ALL keys in input to find where city is stored
  console.log('🔍 Transform input - ALL KEYS:', Object.keys(claimFormB));
  console.log('🔍 Transform input - hospital object:', claimFormB.hospital);
  console.log('🔍 Transform input - checking declaration sources:', {
    'hospitalDeclaration': claimFormB.hospitalDeclaration,
    'declaration': claimFormB.declaration,
    'declarationPlace': claimFormB.declarationPlace,
    'nonNetworkHospitalCity': claimFormB.nonNetworkHospitalCity,
    'nonNetworkDetails': claimFormB.nonNetworkDetails,
    'hospitalDetails': claimFormB.hospitalDetails,
    'hospital': claimFormB.hospital,
    'hospital.address?.city': claimFormB.hospital?.address?.city
  });

  // Extract city from various possible locations
  const hospitalCity = claimFormB.hospitalDeclaration?.declarationPlace || 
                       claimFormB.declaration?.place || 
                       claimFormB.declarationPlace ||
                       claimFormB.nonNetworkHospitalCity ||
                       claimFormB.nonNetworkDetails?.city ||
                       claimFormB.hospitalDetails?.city ||
                       claimFormB.hospitalDetails?.address?.city ||
                       claimFormB.hospital?.address?.city ||  // From saved form backend data
                       '';

  const result = {
    hospitalDetails: {
      name: claimFormB.hospitalDetails?.hospitalName || claimFormB.hospitalDetails?.name || claimFormB.hospitalName || claimFormB.hospital?.name || '',
      hospId: claimFormB.hospitalDetails?.hospitalId || claimFormB.hospitalDetails?.hospId || claimFormB.hospitalId || claimFormB.hospital?.hospitalCode || '',
      typeOfHospital: claimFormB.hospitalDetails?.hospitalType || claimFormB.hospitalDetails?.typeOfHospital || claimFormB.hospitalType || 'network',
      treatingDoctorName: claimFormB.hospitalDetails?.treatingDoctor || claimFormB.hospitalDetails?.treatingDoctorName || claimFormB.treatingDoctor || '',
      treatingDoctorQualification: claimFormB.hospitalDetails?.doctorQualification || claimFormB.hospitalDetails?.treatingDoctorQualification || claimFormB.doctorQualification || '',
      treatingDoctorRegNo: claimFormB.hospitalDetails?.stateRegistrationNo || claimFormB.hospitalDetails?.treatingDoctorRegNo || claimFormB.stateRegistrationNo || '',
      treatingDoctorPhone: claimFormB.hospitalDetails?.hospitalPhone || claimFormB.hospitalDetails?.treatingDoctorPhone || claimFormB.hospitalPhone || '',
      city: claimFormB.hospitalDetails?.city || claimFormB.nonNetworkHospitalCity || ''
    },
    patientDetails: {
      name: claimFormB.patientDetails?.patientName || claimFormB.patientDetails?.name || claimFormB.patientName || '',
      ipRegistrationNo: claimFormB.patientDetails?.ipRegistrationNumber || claimFormB.patientDetails?.ipRegistrationNo || claimFormB.ipRegistrationNumber || '',
      gender: (claimFormB.patientDetails?.gender || claimFormB.gender || '').charAt(0).toUpperCase() + (claimFormB.patientDetails?.gender || claimFormB.gender || '').slice(1),
      ageYears: String(claimFormB.patientDetails?.age || claimFormB.patientDetails?.ageYears || ''),
      ageMonths: String(claimFormB.patientDetails?.ageMonths || ''),
      dateOfBirth: formatISODate(claimFormB.patientDetails?.dateOfBirth) || claimFormB.patientDetails?.dateOfBirth || ''
    },
    insuranceDetails: {
      claimNo: claimFormB.insuranceDetails?.claimNo || ''
    },
    admissionDetails: {
      dateOfAdmission: formatISODate(claimFormB.patientDetails?.dateOfAdmission) || claimFormB.admissionDetails?.dateOfAdmission || '',
      timeOfAdmission: claimFormB.patientDetails?.timeOfAdmission || claimFormB.admissionDetails?.timeOfAdmission || '',
      dateOfDischarge: formatISODate(claimFormB.patientDetails?.dateOfDischarge) || claimFormB.admissionDetails?.dateOfDischarge || '',
      timeOfDischarge: claimFormB.patientDetails?.timeOfDischarge || claimFormB.admissionDetails?.timeOfDischarge || '',
      typeOfAdmission: claimFormB.patientDetails?.typeOfAdmission || claimFormB.admissionDetails?.typeOfAdmission || '',
      isMaternity: (claimFormB.patientDetails?.typeOfAdmission || claimFormB.admissionDetails?.typeOfAdmission) === 'maternity' || claimFormB.admissionDetails?.isMaternity || false,
      dateOfDelivery: formatISODate(claimFormB.patientDetails?.dateOfDelivery) || claimFormB.admissionDetails?.dateOfDelivery || '',
      gravidaStatus: claimFormB.patientDetails?.gravidaStatus || claimFormB.admissionDetails?.gravidaStatus || '',
      statusAtDischarge: claimFormB.patientDetails?.dischargeStatus || claimFormB.admissionDetails?.statusAtDischarge || '',
      totalClaimedAmount: String(claimFormB.patientDetails?.totalClaimedAmount || claimFormB.admissionDetails?.totalClaimedAmount || '')
    },
    ailmentDetails: {
      primaryDiagnosis: {
        icdCode: claimFormB.ailmentDetails?.primaryDiagnosis?.code || claimFormB.ailmentDetails?.primaryDiagnosis?.icdCode || '',
        description: claimFormB.ailmentDetails?.primaryDiagnosis?.description || '',
        description2: claimFormB.ailmentDetails?.primaryDiagnosis?.description2 || ''
      },
      additionalDiagnosis: {
        icdCode: claimFormB.ailmentDetails?.additionalDiagnosis?.code || claimFormB.ailmentDetails?.additionalDiagnosis?.icdCode || '',
        description: claimFormB.ailmentDetails?.additionalDiagnosis?.description || '',
        description2: claimFormB.ailmentDetails?.additionalDiagnosis?.description2 || ''
      },
      coMorbidities1: {
        icdCode: claimFormB.ailmentDetails?.comorbidities?.[0]?.code || claimFormB.ailmentDetails?.coMorbidities1?.icdCode || '',
        description: claimFormB.ailmentDetails?.comorbidities?.[0]?.description || claimFormB.ailmentDetails?.coMorbidities1?.description || '',
        description2: claimFormB.ailmentDetails?.comorbidities?.[0]?.description2 || claimFormB.ailmentDetails?.coMorbidities1?.description2 || ''
      },
      coMorbidities2: {
        icdCode: claimFormB.ailmentDetails?.comorbidities?.[1]?.code || claimFormB.ailmentDetails?.coMorbidities2?.icdCode || '',
        description: claimFormB.ailmentDetails?.comorbidities?.[1]?.description || claimFormB.ailmentDetails?.coMorbidities2?.description || '',
        description2: claimFormB.ailmentDetails?.comorbidities?.[1]?.description2 || claimFormB.ailmentDetails?.coMorbidities2?.description2 || ''
      },
      procedure1: {
        icdPcs: claimFormB.ailmentDetails?.procedures?.[0]?.code || claimFormB.ailmentDetails?.procedure1?.icdPcs || '',
        description: claimFormB.ailmentDetails?.procedures?.[0]?.description || claimFormB.ailmentDetails?.procedure1?.description || '',
        description2: claimFormB.ailmentDetails?.procedures?.[0]?.description2 || claimFormB.ailmentDetails?.procedure1?.description2 || ''
      },
      procedure2: {
        icdPcs: claimFormB.ailmentDetails?.procedures?.[1]?.code || claimFormB.ailmentDetails?.procedure2?.icdPcs || '',
        description: claimFormB.ailmentDetails?.procedures?.[1]?.description || claimFormB.ailmentDetails?.procedure2?.description || '',
        description2: claimFormB.ailmentDetails?.procedures?.[1]?.description2 || claimFormB.ailmentDetails?.procedure2?.description2 || ''
      },
      procedure3: {
        icdPcs: claimFormB.ailmentDetails?.procedures?.[2]?.code || claimFormB.ailmentDetails?.procedure3?.icdPcs || '',
        description: claimFormB.ailmentDetails?.procedures?.[2]?.description || claimFormB.ailmentDetails?.procedure3?.description || '',
        description2: claimFormB.ailmentDetails?.procedures?.[2]?.description2 || claimFormB.ailmentDetails?.procedure3?.description2 || ''
      },
      procedureDetails: claimFormB.ailmentDetails?.procedureDetails || '',
      procedureDetails2: claimFormB.ailmentDetails?.procedureDetails2 || ''
    },
    preAuthorizationDetails: {
      obtained: claimFormB.ailmentDetails?.preAuthObtained === 'yes' || claimFormB.preAuthorizationDetails?.obtained || false,
      authorizationNumber: claimFormB.ailmentDetails?.preAuthNumber || claimFormB.preAuthorizationDetails?.authorizationNumber || ''
    },
    networkAuthReason: claimFormB.ailmentDetails?.authNotObtainedReason || claimFormB.networkAuthReason || '',
    hospitalizationInjury: {
      isInjury: claimFormB.ailmentDetails?.hospitalizationDueToInjury === 'yes' || claimFormB.hospitalizationInjury?.isInjury || false,
      isSelfInflicted: claimFormB.ailmentDetails?.injuryCause === 'self_inflicted' || claimFormB.ailmentDetails?.injuryCause === 'self-inflicted' || claimFormB.hospitalizationInjury?.isSelfInflicted || false,
      isRoadTrafficAccident: claimFormB.ailmentDetails?.injuryCause === 'rta' || claimFormB.ailmentDetails?.injuryCause === 'road_traffic_accident' || claimFormB.hospitalizationInjury?.isRoadTrafficAccident || false,
      isSubstanceAbuse: claimFormB.ailmentDetails?.injuryCause === 'substance_abuse' || claimFormB.ailmentDetails?.injuryCause === 'substance-abuse' || claimFormB.hospitalizationInjury?.isSubstanceAbuse || false
    },
    injurySubstanceAbuse: {
      testConducted: claimFormB.ailmentDetails?.substanceAbuseTestConducted === 'yes' || claimFormB.injurySubstanceAbuse?.testConducted || false,
      isMedicoLegal: claimFormB.ailmentDetails?.isMedicoLegal === 'yes' || claimFormB.injurySubstanceAbuse?.isMedicoLegal || false,
      reportedToPolice: claimFormB.ailmentDetails?.reportedToPolice === 'yes' || claimFormB.injurySubstanceAbuse?.reportedToPolice || false,
      firNo: claimFormB.ailmentDetails?.firNumber || claimFormB.injurySubstanceAbuse?.firNo || '',
      notReportedReason: claimFormB.ailmentDetails?.policeNotReportedReason || claimFormB.injurySubstanceAbuse?.notReportedReason || ''
    },
    claimDocumentsChecklist: {
      // Map API response field names to PDF expected field names
      claimFormDulySigned: claimFormB.documents?.claimFormSigned || claimFormB.claimDocumentsChecklist?.claimFormDulySigned || false,
      originalPreAuthorizationRequest: claimFormB.documents?.preAuthRequest || claimFormB.claimDocumentsChecklist?.originalPreAuthorizationRequest || false,
      copyOfPreAuthorizationApprovalLetter: claimFormB.documents?.preAuthApproval || claimFormB.claimDocumentsChecklist?.copyOfPreAuthorizationApprovalLetter || false,
      copyOfPhotoIdCard: claimFormB.documents?.photoIdVerified || claimFormB.claimDocumentsChecklist?.copyOfPhotoIdCard || false,
      hospitalDischargeSummary: claimFormB.documents?.dischargeSummary || claimFormB.claimDocumentsChecklist?.hospitalDischargeSummary || false,
      operationTheatreNotes: claimFormB.documents?.otNotes || claimFormB.claimDocumentsChecklist?.operationTheatreNotes || false,
      hospitalMainBill: claimFormB.documents?.hospitalMainBill || claimFormB.claimDocumentsChecklist?.hospitalMainBill || false,
      hospitalBreakUpBill: claimFormB.documents?.hospitalBreakupBill || claimFormB.claimDocumentsChecklist?.hospitalBreakUpBill || false,
      investigationReports: claimFormB.documents?.investigationReports || claimFormB.claimDocumentsChecklist?.investigationReports || false,
      ctMrUsgHpeReports: claimFormB.documents?.ctMrUsgHpeReports || claimFormB.claimDocumentsChecklist?.ctMrUsgHpeReports || false,
      doctorsReferenceSlip: claimFormB.documents?.doctorReferenceSlip || claimFormB.claimDocumentsChecklist?.doctorsReferenceSlip || false,
      ecg: claimFormB.documents?.ecg || claimFormB.claimDocumentsChecklist?.ecg || false,
      pharmacyBills: claimFormB.documents?.pharmacyBills || claimFormB.claimDocumentsChecklist?.pharmacyBills || false,
      mlcReportsAndPoliceFir: claimFormB.documents?.mlcReportsAndFir || claimFormB.claimDocumentsChecklist?.mlcReportsAndPoliceFir || false,
      originalDeathSummary: claimFormB.documents?.deathSummary || claimFormB.claimDocumentsChecklist?.originalDeathSummary || false,
      anyOther: claimFormB.documents?.others || claimFormB.claimDocumentsChecklist?.anyOther || false,
      anyOtherDetails: claimFormB.documents?.othersSpecify || claimFormB.claimDocumentsChecklist?.anyOtherDetails || ''
    },
    nonNetworkHospitalDetails: {
      // Map API response field names properly
      addressLine1: claimFormB.nonNetworkDetails?.address || claimFormB.nonNetworkHospitalDetails?.addressLine1 || '',
      addressLine2: claimFormB.nonNetworkDetails?.addressLine2 || claimFormB.nonNetworkHospitalDetails?.addressLine2 || '',
      city: claimFormB.nonNetworkDetails?.city || claimFormB.nonNetworkHospitalDetails?.city || '',
      state: claimFormB.nonNetworkDetails?.state || claimFormB.nonNetworkHospitalDetails?.state || '',
      pinCode: claimFormB.nonNetworkDetails?.pinCode || claimFormB.nonNetworkHospitalDetails?.pinCode || '',
      phoneNo: claimFormB.nonNetworkDetails?.phone || claimFormB.nonNetworkHospitalDetails?.phoneNo || '',
      registrationNo: claimFormB.nonNetworkDetails?.registrationNo || claimFormB.nonNetworkHospitalDetails?.registrationNo || '',
      hospitalPan: claimFormB.nonNetworkDetails?.pan || claimFormB.nonNetworkHospitalDetails?.hospitalPan || '',
      numberOfInpatientBeds: String(claimFormB.nonNetworkDetails?.inpatientBeds || claimFormB.nonNetworkHospitalDetails?.numberOfInpatientBeds || ''),
      facilitiesOT: claimFormB.nonNetworkDetails?.hasFacilityOT || claimFormB.nonNetworkHospitalDetails?.facilitiesOT || false,
      facilitiesICU: claimFormB.nonNetworkDetails?.hasFacilityICU || claimFormB.nonNetworkHospitalDetails?.facilitiesICU || false,
      otherFacilities: claimFormB.nonNetworkDetails?.otherFacilities || claimFormB.nonNetworkHospitalDetails?.otherFacilities || ''
    },
    hospitalDeclaration: {
      // Map declaration fields
      declarationDate: claimFormB.hospitalDeclaration?.declarationDate || 
                       claimFormB.declaration?.date || 
                       claimFormB.declarationDate ||
                       formatTodayDate(),
      // Use the hospitalCity extracted earlier which checks all locations
      declarationPlace: hospitalCity
    }
  };
  
  // Debug log to verify declaration data
  console.log('🏥 Transform - Final declaration:', {
    'hospitalCity extracted': hospitalCity,
    'hospital.address.city': claimFormB.hospital?.address?.city,
    'result.hospitalDeclaration': result.hospitalDeclaration
  });
  
  return result;
};

/**
 * Generate the PDF by writing directly on the template
 * @param {Object} inputData - The form data in your JSON structure
 * @returns {Promise<Uint8Array>} - PDF bytes
 */
export const generateClaimFormBPdf = async (inputData) => {
  try {
    // Transform the data to expected format
    const data = transformData(inputData);
    
    // Debug: Log the transformed data
    console.log('🔄 Transformed data for PDF generation:', {
      hospitalName: data.hospitalDetails.name,
      patientName: data.patientDetails.name,
      dateOfBirth: data.patientDetails.dateOfBirth,
      dateOfAdmission: data.admissionDetails.dateOfAdmission,
      primaryDiagnosis: data.ailmentDetails.primaryDiagnosis,
      documents: data.claimDocumentsChecklist
    });

    // Fetch the PDF template
    const existingPdfBytes = await fetch(pdfTemplate).then(res => res.arrayBuffer());
    
    // Load the PDF
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    
    // Embed font
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Get the first page
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    const { width, height } = firstPage.getSize();

    // Helper function to draw text at specific position on a given page
    const drawTextOnPage = (page, text, x, y, options = {}) => {
      const textStr = String(text || '');
      page.drawText(textStr, {
        x,
        y,
        size: options.size || 8,
        font: options.bold ? boldFont : font,
        color: rgb(0, 0, 0),
      });
    };

    // Helper function to draw text on first page
    const drawText = (text, x, y, options = {}) => {
      drawTextOnPage(firstPage, text, x, y, options);
    };

    // Helper to draw text in character boxes
    const drawCharacterBoxesOnPage = (page, text, startX, y, charWidthOrOptions = 10.75, options = {}) => {
      let charWidth = charWidthOrOptions;
      let opts = options;
      let customSpacing = false;
      
      if (typeof charWidthOrOptions === 'object') {
        charWidth = 10.75;
        opts = charWidthOrOptions;
      } else if (typeof charWidthOrOptions === 'number' && charWidthOrOptions !== 10.75) {
        customSpacing = true;
      }
      
      const textStr = String(text || '').toUpperCase();
      let currentX = charWidth;
      
      for (let i = 0; i < textStr.length; i++) {
        const char = textStr[i];
        const isNumeric = /[0-9]/.test(char);
        const isSpace = char === ' ';
        
        let charSpacing;
        if (customSpacing) {
          charSpacing = charWidth;
        } else {
          charSpacing = 11.45;
          if (isNumeric) {
            charSpacing = 11.69;
          } else if (isSpace) {
            charSpacing = 11.69;
          }
        }
        
        if (i > 0) {
          currentX += charSpacing;
        }
        
        drawTextOnPage(page, char, startX + currentX, y, opts);
      }
    };

    // Helper for first page
    const drawCharacterBoxes = (text, startX, y, charWidthOrOptions = 10.75, options = {}) => {
      drawCharacterBoxesOnPage(firstPage, text, startX, y, charWidthOrOptions, options);
    };

    // Helper to draw a checkbox tick mark
    const drawCheckboxOnPage = (page, isChecked, x, y, options = {}) => {
      if (isChecked) {
        const size = options.size || 8;
        page.drawLine({
          start: { x: x + 1, y: y + size/2 },
          end: { x: x + size/3, y: y + 1 },
          thickness: 1.5,
          color: rgb(0, 0, 0),
        });
        page.drawLine({
          start: { x: x + size/3, y: y + 1 },
          end: { x: x + size - 1, y: y + size - 1 },
          thickness: 1.5,
          color: rgb(0, 0, 0),
        });
      }
    };

    // Helper for first page checkbox
    const drawCheckbox = (isChecked, x, y, options = {}) => {
      drawCheckboxOnPage(firstPage, isChecked, x, y, options);
    };

    // ============ PAGE 1 ============
    
    // ============ SECTION A - DETAILS OF HOSPITAL ============
    const hospitalDetails = data.hospitalDetails;
    
    // a) Name of Hospital
    drawCharacterBoxes(hospitalDetails.name || '', 116, 717, { size: 7 });
    
    // b) Hospital ID
    drawCharacterBoxes(hospitalDetails.hospId || '', 116, 702, { size: 7 });
    
    // c) Type of Hospital - Network / Non Network checkboxes
    drawCheckbox(hospitalDetails.typeOfHospital === 'network' || hospitalDetails.typeOfHospital === 'Network', 348, 701, { size: 7 });
    drawCheckbox(hospitalDetails.typeOfHospital === 'non-network' || hospitalDetails.typeOfHospital === 'Non Network', 415, 701, { size: 7 });
    
    // d) Name of the treating doctor
    drawCharacterBoxes(formatName(hospitalDetails.treatingDoctorName) || '', 127, 687, { size: 7 });
    
    // e) Qualification
    drawText(hospitalDetails.treatingDoctorQualification || '', 100, 674, { size: 6 });
    
    // f) Registration No. with State Code
    drawCharacterBoxes(hospitalDetails.treatingDoctorRegNo || '', 294, 674, { size: 7 });
    
    // g) Phone No.
    drawCharacterBoxes(hospitalDetails.treatingDoctorPhone || '', 426, 674, { size: 7 });

    // ============ SECTION B - DETAILS OF PATIENT ADMITTED ============
    const patientDetails = data.patientDetails;
    const insuranceDetails = data.insuranceDetails;
    const admissionDetails = data.admissionDetails;
    
    // a) Name of the Patient
    drawCharacterBoxes(formatName(patientDetails.name) || '', 116, 639, { size: 7 });
    
    // b) IP Registration Number
    drawCharacterBoxes(zeroPad(patientDetails.ipRegistrationNo, 8) || '', 116, 626, { size: 7 });
    
    // c) Gender - Male/Female checkboxes
    drawCheckbox(patientDetails.gender === 'Male', 270, 624, { size: 7 });
    drawCheckbox(patientDetails.gender === 'Female', 303, 624, { size: 7 });
    
    // d) Age: Years
    drawCharacterBoxes(zeroPad(patientDetails.ageYears, 2) || '', 350, 624, { size: 7 });
    
    // d) Age: Months
    drawCharacterBoxes(zeroPad(patientDetails.ageMonths, 2) || '', 393, 624, { size: 7 });
    
    // e) Date of birth DD/MM/YYYY
    if (patientDetails.dateOfBirth) {
      const dobParts = patientDetails.dateOfBirth.split('/');
      if (dobParts.length === 3) {
        drawCharacterBoxes(dobParts[0], 460, 624, { size: 7 });
        drawCharacterBoxes(dobParts[1], 493, 624, { size: 7 });
        drawCharacterBoxes(dobParts[2].slice(2,4), 528, 624, { size: 7 });
      }
    }
    
    // f) Date of Admission DD/MM/YYYY
    if (admissionDetails.dateOfAdmission) {
      const admParts = admissionDetails.dateOfAdmission.split('/');
      if (admParts.length === 3) {
        drawCharacterBoxes(admParts[0], 116, 611, { size: 7 });
        drawCharacterBoxes(admParts[1], 150, 611, { size: 7 });
        drawCharacterBoxes(admParts[2].slice(2,4), 182, 611, { size: 7 });
      }
    }
    
    // g) Time of Admission
    if (admissionDetails.timeOfAdmission) {
      const timeParts = admissionDetails.timeOfAdmission.split(':');
      if (timeParts.length >= 2) {
        drawCharacterBoxes(timeParts[0], 239, 611, { size: 7 });
        drawCharacterBoxes(timeParts[1], 272, 611, { size: 7 });
      }
    }
    
    // h) Date of Discharge DD/MM/YYYY
    if (admissionDetails.dateOfDischarge) {
      const disParts = admissionDetails.dateOfDischarge.split('/');
      if (disParts.length === 3) {
        drawCharacterBoxes(disParts[0], 372, 611, { size: 7 });
        drawCharacterBoxes(disParts[1], 405, 611, { size: 7 });
        drawCharacterBoxes(disParts[2].slice(2,4), 439, 611, { size: 7 });
      }
    }
    
    // i) Time of Discharge
    if (admissionDetails.timeOfDischarge) {
      const timeParts = admissionDetails.timeOfDischarge.split(':');
      if (timeParts.length >= 2) {
        drawCharacterBoxes(timeParts[0], 495, 611, { size: 7 });
        drawCharacterBoxes(timeParts[1], 529, 611, { size: 7 });
      }
    }
    
    // j) Type of Admission - Emergency/Planned/Day Care/Maternity checkboxes
    const typeOfAdmission = admissionDetails.typeOfAdmission || '';
    drawCheckbox(typeOfAdmission === 'emergency' || typeOfAdmission === 'Emergency', 148, 593, { size: 7 });
    drawCheckbox(typeOfAdmission === 'planned' || typeOfAdmission === 'Planned', 182, 593, { size: 7 });
    drawCheckbox(typeOfAdmission === 'day_care' || typeOfAdmission === 'daycare' || typeOfAdmission === 'Day Care', 222, 593, { size: 7 });
    drawCheckbox(typeOfAdmission === 'maternity' || typeOfAdmission === 'Maternity', 260, 593, { size: 7 });
    
    // k) If Maternity - Date of Delivery
    if (admissionDetails.isMaternity && admissionDetails.dateOfDelivery) {
      const delParts = admissionDetails.dateOfDelivery.split('/');
      if (delParts.length === 3) {
        drawCharacterBoxes(delParts[0], 372, 595, { size: 7 });
        drawCharacterBoxes(delParts[1], 405, 595, { size: 7 });
        drawCharacterBoxes(delParts[2].slice(2,4), 439, 595, { size: 7 });

        drawCharacterBoxes(admissionDetails.gravidaStatus || '', 516, 595, { size: 7 });
      }
    }
    
    // l) Status at time of discharge checkboxes
    const statusAtDischarge = admissionDetails.statusAtDischarge || '';
    drawCheckbox(statusAtDischarge === 'home' || statusAtDischarge === 'Discharge to home', 182, 578, { size: 7 });
    drawCheckbox(statusAtDischarge === 'another_hospital' || statusAtDischarge === 'Discharge to another hospital', 270, 578, { size: 7 });
    drawCheckbox(statusAtDischarge === 'deceased' || statusAtDischarge === 'Deceased', 314, 578, { size: 7 });
    
    // m) Total claimed amount
    drawCharacterBoxes(zeroPad(admissionDetails.totalClaimedAmount, 7) || '', 470, 579, { size: 7 });

    // ============ SECTION C - DETAILS OF AILMENT DIAGNOSED (PRIMARY) ============
    const ailmentDetails = data.ailmentDetails;
    const preAuthDetails = data.preAuthorizationDetails;
    const injuryDetails = data.hospitalizationInjury;
    const injurySubstanceDetails = data.injurySubstanceAbuse;
    
    // a) i. Primary Diagnosis - ICD 10 Codes
    drawCharacterBoxes(ailmentDetails.primaryDiagnosis.icdCode || '', 115, 527, { size: 7 });
    // Primary Diagnosis - Description
    drawText(ailmentDetails.primaryDiagnosis.description || '', 215, 527, { size: 6 });
    drawText(ailmentDetails.primaryDiagnosis.description2 || '', 215, 519, { size: 6 });
    
    // a) ii. Additional Diagnosis - ICD 10 Codes
    drawCharacterBoxes(ailmentDetails.additionalDiagnosis.icdCode || '', 115, 502, { size: 7 });
    // Additional Diagnosis - Description
    drawText(ailmentDetails.additionalDiagnosis.description || '', 215, 502, { size: 6 });
    drawText(ailmentDetails.additionalDiagnosis.description2 || '', 215, 494, { size: 6 });
    
    // a) iii. Co-morbidities - ICD 10 Codes
    drawCharacterBoxes(ailmentDetails.coMorbidities1.icdCode || '', 115, 476, { size: 7 });
    // Co-morbidities - Description
    drawText(ailmentDetails.coMorbidities1.description || '', 215, 476, { size: 6 });
    drawText(ailmentDetails.coMorbidities1.description2 || '', 215, 468, { size: 6 }); 
    
    // a) iv. Co-morbidities - ICD 10 Codes
    drawCharacterBoxes(ailmentDetails.coMorbidities2.icdCode || '', 115, 450, { size: 7 });
    // Co-morbidities - Description
    drawText(ailmentDetails.coMorbidities2.description || '', 215, 450, { size: 6 });
    drawText(ailmentDetails.coMorbidities2.description2 || '', 215, 442, { size: 6 });
    
    // b) i. Procedure 1 - ICD 10 PCS
    drawCharacterBoxes(ailmentDetails.procedure1.icdPcs || '', 368, 527, { size: 7 });
    // Procedure 1 - Description
    drawText(ailmentDetails.procedure1.description || '', 466, 527, { size: 6 });
    drawText(ailmentDetails.procedure1.description2 || '', 466, 519, { size: 6 });
    
    // b) ii. Procedure 2 - ICD 10 PCS
    drawCharacterBoxes(ailmentDetails.procedure2.icdPcs || '', 368, 501, { size: 7 });
    // Procedure 2 - Description
    drawText(ailmentDetails.procedure2.description || '', 466, 502, { size: 6 });
    drawText(ailmentDetails.procedure2.description2 || '', 466, 494, { size: 6 });
    
    // b) iii. Procedure 3 - ICD 10 PCS
    drawCharacterBoxes(ailmentDetails.procedure3.icdPcs || '', 368, 476, { size: 7 });
    // Procedure 3 - Description
    drawText(ailmentDetails.procedure3.description || '', 466, 476, { size: 6 });
    drawText(ailmentDetails.procedure3.description2 || '', 466, 468, { size: 6 });
    
    // b) iv. Details of Procedure
    drawText(ailmentDetails.procedureDetails || '', 680, 493, { size: 6 });
    drawText(ailmentDetails.procedureDetails ? ailmentDetails.procedureDetails.substring(50) : '', 382, 450, { size: 6 });
    drawText(ailmentDetails.procedureDetails2 ? ailmentDetails.procedureDetails2.substring(50) : '', 382, 442, { size: 6 });
    
    // c) Pre-authorization obtained - Yes/No checkboxes
    drawCheckbox(preAuthDetails.obtained === true, 214, 417, { size: 7 });
    drawCheckbox(preAuthDetails.obtained === false, 239, 416, { size: 7 });
    
    // d) Pre-authorization Number
    if (preAuthDetails.obtained && preAuthDetails.authorizationNumber) {
      const preAuthNum = String(preAuthDetails.authorizationNumber || '').substring(0, 13);
      drawCharacterBoxes(preAuthNum, 337, 417, { size: 7 });
    }
    
    // e) If authorization by network hospital not obtained, give reason
    if (!preAuthDetails.obtained && data.networkAuthReason) {
      drawText(data.networkAuthReason, 214, 402, { size: 6 });
    }
    
    // f) Hospitalization due to injury - Yes/No checkboxes
    drawCheckbox(injuryDetails.isInjury === true, 134, 384, { size: 7 });
    drawCheckbox(injuryDetails.isInjury === false, 159, 384, { size: 7 });
    
    // If Yes, give cause checkboxes
    if (injuryDetails.isInjury) {
      drawCheckbox(injuryDetails.isSelfInflicted === true, 291, 384, { size: 7 });
      drawCheckbox(injuryDetails.isRoadTrafficAccident === true, 370, 384, { size: 7 });
      drawCheckbox(injuryDetails.isSubstanceAbuse === true, 504, 384, { size: 7 });
    }
    
    // ii) If Injury due to substance abuse - Test conducted - Yes/No
    if (injuryDetails.isSubstanceAbuse) {
      drawCheckbox(injurySubstanceDetails.testConducted === true, 269, 366, { size: 7 });
      drawCheckbox(injurySubstanceDetails.testConducted === false, 291, 366, { size: 7 });
    }
    
    // iii) If Medico legal - Yes/No
    drawCheckbox(injurySubstanceDetails.isMedicoLegal === true, 413, 366, { size: 7 });
    drawCheckbox(injurySubstanceDetails.isMedicoLegal === false, 436, 366, { size: 7 });
    
    // iv) Reported to Police - Yes/No
    drawCheckbox(injurySubstanceDetails.reportedToPolice === true, 516, 366, { size: 7 });
    drawCheckbox(injurySubstanceDetails.reportedToPolice === false, 539, 366, { size: 7 });
    
    // v) FIR No.
    if (injurySubstanceDetails.reportedToPolice && injurySubstanceDetails.firNo) {
      drawCharacterBoxes(injurySubstanceDetails.firNo, 77, 351, { size: 7 });
    }
    
    // vi) If not reported to police give reason
    if (!injurySubstanceDetails.reportedToPolice && injurySubstanceDetails.notReportedReason) {
      drawText(injurySubstanceDetails.notReportedReason, 323, 352, { size: 6 });
    }

    // ============ SECTION D - CLAIM DOCUMENTS SUBMITTED CHECKLIST ============
    const claimDocs = data.claimDocumentsChecklist || {};
    
    // Left column checkboxes
    drawCheckbox(claimDocs.claimFormDulySigned, 71, 311, { size: 7 });
    drawCheckbox(claimDocs.originalPreAuthorizationRequest, 71, 299, { size: 7 });
    drawCheckbox(claimDocs.copyOfPreAuthorizationApprovalLetter, 71, 287, { size: 7 });
    drawCheckbox(claimDocs.copyOfPhotoIdCard, 71, 277, { size: 7 });
    drawCheckbox(claimDocs.hospitalDischargeSummary, 71, 266, { size: 7 });
    drawCheckbox(claimDocs.operationTheatreNotes, 71, 254, { size: 7 });
    drawCheckbox(claimDocs.hospitalMainBill, 71, 244, { size: 7 });
    drawCheckbox(claimDocs.hospitalBreakUpBill, 71, 232, { size: 7 });
    
    // Right column checkboxes
    drawCheckbox(claimDocs.investigationReports, 330, 311, { size: 7 });
    drawCheckbox(claimDocs.ctMrUsgHpeReports, 330, 299, { size: 7 });
    drawCheckbox(claimDocs.doctorsReferenceSlip, 330, 287, { size: 7 });
    drawCheckbox(claimDocs.ecg, 330, 277, { size: 7 });
    drawCheckbox(claimDocs.pharmacyBills, 330, 266, { size: 7 });
    drawCheckbox(claimDocs.mlcReportsAndPoliceFir, 330, 254, { size: 7 });
    drawCheckbox(claimDocs.originalDeathSummary, 330, 244, { size: 7 });
    drawCheckbox(claimDocs.anyOther, 330, 232, { size: 7 });
    
    // If any other is checked, add the details text
    if (claimDocs.anyOther && claimDocs.anyOtherDetails) {
      drawText(claimDocs.anyOtherDetails, 407, 234, { size: 6 });
    }
    
    // ============ SECTION E - ADDITIONAL DETAILS IN CASE OF NON NETWORK HOSPITAL ============
    const nonNetworkDetails = data.nonNetworkHospitalDetails || {};
    const isNonNetworkHospital = data.hospitalDetails?.typeOfHospital === 'non-network';
    
    // a) Address of the Hospital
    drawCharacterBoxes(nonNetworkDetails.addressLine1 || '', 114, 192, { size: 7 });
    drawCharacterBoxes(nonNetworkDetails.addressLine2 || '', 114, 178, { size: 7 });
    drawCharacterBoxes(nonNetworkDetails.city || '', 125, 164, { size: 7 });
    drawCharacterBoxes(nonNetworkDetails.state || '', 344, 164, { size: 7 });
    drawCharacterBoxes(nonNetworkDetails.pinCode || '', 135, 151, { size: 7 });
    
    // b) Phone No.
    drawCharacterBoxes(nonNetworkDetails.phoneNo || '', 247, 151, { size: 7 });
    
    // c) Registration No. with State Code
    drawCharacterBoxes(nonNetworkDetails.registrationNo || '', 458, 151, { size: 7 });
    
    // d) Hospital PAN
    drawCharacterBoxes(nonNetworkDetails.hospitalPan || '', 111, 136, { size: 7 });
    
    // e) Number of inpatient beds
    drawCharacterBoxes(nonNetworkDetails.numberOfInpatientBeds || '', 292, 136, { size: 7 });
    
    // f) Facilities available in the hospital - ONLY for non-network hospitals
    if (isNonNetworkHospital) {
      // i. OT - Yes/No
      drawCheckbox(nonNetworkDetails.facilitiesOT === true, 449, 136, { size: 7 });
      drawCheckbox(nonNetworkDetails.facilitiesOT === false, 472, 136, { size: 7 });
      
      // ii. ICU - Yes/No
      drawCheckbox(nonNetworkDetails.facilitiesICU === true, 517, 136, { size: 7 });
      drawCheckbox(nonNetworkDetails.facilitiesICU === false, 537, 136, { size: 7 });
    }
    
    // iii. Others
    if (nonNetworkDetails.otherFacilities) {
      drawText(nonNetworkDetails.otherFacilities, 124, 123, { size: 6 });
    }

    // ============ SECTION F - DECLARATION BY THE HOSPITAL ============
    const hospitalDeclaration = data.hospitalDeclaration || {};
    
    console.log('📝 Section F Declaration data:', hospitalDeclaration);
    
    // Date DD/MM/YYYY
    if (hospitalDeclaration.declarationDate) {
      const dateParts = hospitalDeclaration.declarationDate.split('/');
      if (dateParts.length === 3) {
        drawCharacterBoxes(dateParts[0], 75, 46, { size: 7 });
        drawCharacterBoxes(dateParts[1], 111, 46, { size: 7 });
        drawCharacterBoxes(dateParts[2].slice(-2), 145, 46, { size: 7 });
      }
    }
    
    // Place - use character boxes to match the PDF template
    if (hospitalDeclaration.declarationPlace) {
      drawText(hospitalDeclaration.declarationPlace,88, 28, { size: 7 });
    } else {
      console.log('⚠️ No declaration place found!');
    }

    // Save and return the PDF bytes
    const pdfBytes = await pdfDoc.save();
    return pdfBytes;

  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Download the claim form as PDF directly
 */
export const downloadClaimFormBPdf = async (data, filename = 'ClaimFormB.pdf') => {
  try {
    const pdfBytes = await generateClaimFormBPdf(data);
    
    // Create blob and download
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return pdfBytes;
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};

// Export transform function for testing
export const transformClaimFormData = transformData;

export default generateClaimFormBPdf;